import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { SpecialSaleService } from '@pulpo/pulpo-api';
import { CurrencyTypeEnum, SpecialSale } from '@pulpo/pulpo-models';
import { PulpoSnackbarComponent } from '../../pulpo-snackbar/pulpo-snackbar.component';
import { AddSpecialSaleDialogComponent } from '../add-special-sale-dialog/add-special-sale-dialog.component';
import { DeleteDialogComponent } from '../../delete-dialog/delete-dialog.component';
import dayjs from 'dayjs';
import { PaymentConditionPipe, PaymentEventTypePipe } from '../../../pipes';

@Component({
  selector: 'special-sales-dialog',
  templateUrl: './special-sales-dialog.component.html',
  styleUrls: ['./special-sales-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatIconModule,
    MatButtonModule,
    PaymentEventTypePipe,
    PaymentConditionPipe,
  ],
})
export class SpecialSalesDialogComponent implements OnInit {
  displayedColumns: string[] = [
    'customerMail',
    'dates',
    'deposit',
    'daysBeforeDeparture',
    'dateSent',
    'dateAccepted',
    'bookingId',
    'options',
  ];
  dataSource: MatTableDataSource<SpecialSale> =
    new MatTableDataSource<SpecialSale>([]);
  CurrencyTypeEnum = CurrencyTypeEnum;

  private specialSaleService = inject(SpecialSaleService);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  private data = inject(MAT_DIALOG_DATA) as { contractId: number };
  private cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.refresh();
  }

  refresh() {
    this.specialSaleService
      .getSalesByContractId(this.data.contractId)
      .subscribe((response) => {
        this.dataSource.data = response.body || [];
        this.cdr.markForCheck();
      });
  }

  deleteSpecialSale(specialSale: SpecialSale) {
    const dialogRef = this.dialog.open(DeleteDialogComponent, {
      width: '30%',
      data: {
        id: specialSale.id,
        content: dayjs().isBefore(specialSale.endDate)
          ? "Cette vente spéciale n'est pas temrinée, voulez vous vraiment la supprimer ?"
          : 'Voulez-vous vraiment supprimer cette vente spéciale ?',
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result && result !== -1) {
        this.specialSaleService.delete(specialSale.id).subscribe(() => {
          this.snackBar.openFromComponent(PulpoSnackbarComponent, {
            data: {
              title: 'Suppression',
              message: 'La vente spéciale a été supprimée',
            },
            duration: 4000,
            panelClass: ['snackbar'],
          });
          this.cdr.markForCheck();
          this.refresh();
          this.cdr.markForCheck();
        });
      }
      this.cdr.markForCheck();
    });
  }

  openAddSaleDialog() {
    const dialogRef = this.dialog.open(AddSpecialSaleDialogComponent, {
      width: '50%',
      panelClass: 'pulpo-dialog-container',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.specialSaleService
          .create({ ...result, contractId: this.data.contractId })
          .subscribe(
            () => {
              this.snackBar.openFromComponent(PulpoSnackbarComponent, {
                data: {
                  title: 'Ajout',
                  message: 'La vente spéciale a été ajoutée',
                },
                duration: 4000,
                panelClass: ['snackbar'],
              });
              this.cdr.markForCheck();
              this.refresh();
              this.cdr.markForCheck();
            },
            (error) => {
              if (error.status === 404) {
                this.snackBar.openFromComponent(PulpoSnackbarComponent, {
                  data: {
                    title: 'Impossible de créer la vente spéciale',
                    message: 'Produit non distribué par traveltribe',
                    backgroundColor: '#ec2b2b',
                    accentColor: 'white',
                  },
                  duration: 4000,
                  panelClass: ['snackbar'],
                });
                this.cdr.markForCheck();
                this.dialog.closeAll();
              }
              this.cdr.markForCheck();
            }
          );
      }
      this.cdr.markForCheck();
    });
  }

  resendMail(specialSale: SpecialSale) {
    this.specialSaleService.sendMail(specialSale).subscribe(() => {
      this.snackBar.openFromComponent(PulpoSnackbarComponent, {
        data: {
          title: 'Email envoyé',
          message: "L'email a été renvoyé",
        },
        duration: 4000,
        panelClass: ['snackbar'],
      });
      this.cdr.markForCheck();
    });
  }

  triggerActivation(specialSale: SpecialSale) {
    this.specialSaleService
      .update({ ...specialSale, active: !specialSale.active })
      .subscribe(() => {
        this.snackBar.openFromComponent(PulpoSnackbarComponent, {
          data: {
            title: 'Activation',
            message:
              'La vente spéciale a été ' +
              (specialSale.active ? 'desactivée' : 'activée'),
          },
          duration: 4000,
          panelClass: ['snackbar'],
        });
        this.cdr.markForCheck();
        this.refresh();
        this.cdr.markForCheck();
      });
  }
}
